/**
 * Body-weight SUV for PET (spec section 3, bottom-left overlay): SUVbw = Cpet · weight / decayed dose,
 * with Cpet the pixel in Bq/ml (Units BQML), the weight in grams and the injected dose decayed from
 * the radiopharmaceutical start time (0018,1072) to the series time (0008,0031, else acquisition
 * time 0008,0032) with the radionuclide half-life (0018,1075).
 */

export const SUV_TEXT = { formula: 'SUV = Cpet * Weight / Dose', unavailable: ' SUV no se puede calcular' } as const;

const SECONDS_PER_DAY = 86_400;

export interface DicomTime {
  hours?: number;
  minutes?: number;
  seconds?: number;
  fractionalSeconds?: number;
}

export interface SuvInputs {
  units: string;
  /** kg */
  weight: number;
  /** Bq */
  totalDose: number;
  /** s */
  halfLife: number;
  /** seconds since midnight */
  injectionTime: number;
  /** seconds since midnight */
  seriesTime: number;
}

/** DICOM TM ("HHMMSS.FFFFFF" or a parsed time) → seconds since midnight; NaN when unknown. */
export function secondsOfDay(value: string | DicomTime | undefined | null): number {
  if (value === undefined || value === null) return NaN;
  if (typeof value === 'object') {
    if (value.hours === undefined) return NaN;
    const fraction = Number(value.fractionalSeconds ?? 0);
    const fractional = fraction >= 1 ? fraction / 10 ** String(Math.trunc(fraction)).length : fraction;
    return value.hours * 3600 + (value.minutes ?? 0) * 60 + (value.seconds ?? 0) + fractional;
  }
  const match = /^(\d{2})(\d{2})?(\d{2})?(\.\d+)?/.exec(value.trim().replace(/:/g, ''));
  if (!match) return NaN;
  return Number(match[1]) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0) + Number(match[4] ?? 0);
}

/** Multiplier from Bq/ml to SUVbw, or null when the header does not allow it. */
export function suvFactor(inputs: SuvInputs): number | null {
  const { units, weight, totalDose, halfLife, injectionTime, seriesTime } = inputs;
  if (units.trim().toUpperCase() !== 'BQML') return null;
  if (![weight, totalDose, halfLife, injectionTime, seriesTime].every((v) => Number.isFinite(v)) || weight <= 0 || totalDose <= 0 || halfLife <= 0) return null;
  let elapsed = seriesTime - injectionTime;
  if (elapsed < 0) elapsed += SECONDS_PER_DAY;
  const decayedDose = totalDose * 2 ** (-elapsed / halfLife);
  return (weight * 1000) / decayedDose;
}
